import { formatDistanceToNow } from 'date-fns';

/**
 * Notification Helper Utilities
 * Colors, icons, formatters
 */

/**
 * Get priority styles (daisyUI semantic colors)
 */
export const getPriorityStyles = (priority) => {
  switch (priority) {
    case 'critical':
      return {
        bg: 'bg-error',
        text: 'text-error',
        border: 'border-error',
        badge: 'badge-error',
        alert: 'alert-error',
      };
    case 'high':
      return {
        bg: 'bg-warning',
        text: 'text-warning',
        border: 'border-warning',
        badge: 'badge-warning',
        alert: 'alert-warning',
      };
    case 'medium':
      return {
        bg: 'bg-info',
        text: 'text-info',
        border: 'border-info',
        badge: 'badge-info',
        alert: 'alert-info',
      };
    case 'low':
    default:
      return {
        bg: 'bg-base-200',
        text: 'text-base-content',
        border: 'border-base-300',
        badge: 'badge-ghost',
        alert: 'alert',
      };
  }
};

/**
 * Get category color
 */
export const getCategoryColor = (category) => {
  switch (category) {
    case 'discipline':
      return 'text-warning';
    case 'administrative':
      return 'text-info';
    case 'communication':
      return 'text-success';
    case 'system':
      return 'text-error';
    default:
      return 'text-base-content';
  }
};

/**
 * Format timestamp to relative time
 */
export const formatNotificationTime = (timestamp) => {
  if (!timestamp) return '';

  try {
    // Handle Firestore Timestamp
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting notification time:', error);
    return '';
  }
};

/**
 * Get priority label
 */
export const getPriorityLabel = (priority) => {
  switch (priority) {
    case 'critical':
      return 'CRITICAL';
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return '';
  }
};

/**
 * Get notification icon component name
 */
export const getIconComponent = (iconName) => {
  // Map icon names to actual Heroicons imports
  // This will be used by components to dynamically import icons
  const iconMap = {
    ExclamationCircleIcon: 'ExclamationCircleIcon',
    ChatBubbleLeftIcon: 'ChatBubbleLeftIcon',
    ShieldExclamationIcon: 'ShieldExclamationIcon',
    BoltIcon: 'BoltIcon',
    UserPlusIcon: 'UserPlusIcon',
    CogIcon: 'CogIcon',
    EnvelopeIcon: 'EnvelopeIcon',
    BellIcon: 'BellIcon',
  };

  return iconMap[iconName] || 'BellIcon';
};

/**
 * Check if notification is unread
 */
export const isUnread = (notification, userId) => {
  return !notification.readBy?.includes(userId);
};

/**
 * Check if notification is actionable
 */
export const isActionable = (notification) => {
  return notification.actionable && notification.actionUrl;
};

/**
 * Generate notification group key
 */
export const generateGroupKey = (type, schoolId, date = new Date()) => {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
  return `${type}_${schoolId}_${dateStr}`;
};

/**
 * Generate student group key (hourly grouping)
 */
export const generateStudentGroupKey = (studentId, schoolId, date = new Date()) => {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
  const hour = date.getHours().toString().padStart(2, '0');
  return `student_${studentId}_${schoolId}_${dateStr}-${hour}`;
};

/**
 * Generate rule group key
 */
export const generateRuleGroupKey = (ruleId, schoolId, date = new Date()) => {
  const dateStr = date.toISOString().split('T')[0];
  return `rule_${ruleId}_${schoolId}_${dateStr}`;
};

/**
 * Generate bulk operation group key
 */
export const generateBulkGroupKey = (operationType, sessionId, schoolId) => {
  return `bulk_${operationType}_${sessionId}_${schoolId}`;
};

/**
 * Sort notifications by priority and date
 */
export const sortNotifications = (notifications) => {
  const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };

  return [...notifications].sort((a, b) => {
    // First sort by priority
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
    if (priorityDiff !== 0) return priorityDiff;

    // Then sort by date (newest first)
    const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
    const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
    return bTime - aTime;
  });
};

/**
 * Filter notifications by criteria
 */
export const filterNotifications = (notifications, filters = {}) => {
  let filtered = [...notifications];

  const { types, priorities, categories, unreadOnly, userId } = filters;

  if (types && types.length > 0) {
    filtered = filtered.filter((n) => types.includes(n.type));
  }

  if (priorities && priorities.length > 0) {
    filtered = filtered.filter((n) => priorities.includes(n.priority));
  }

  if (categories && categories.length > 0) {
    filtered = filtered.filter((n) => categories.includes(n.category));
  }

  if (unreadOnly && userId) {
    filtered = filtered.filter((n) => isUnread(n, userId));
  }

  return filtered;
};
